import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail, API_BASE } from "@/lib/api";
import { formatINR } from "@/lib/format";
import { toast } from "sonner";
import {
  Truck,
  Fuel,
  MapPin,
  CheckCircle2,
  AlertCircle,
  XCircle,
  ArrowLeft,
  Gauge,
  Calendar,
  CreditCard,
} from "lucide-react";

const STATUS = {
  approved: { color: "bg-emerald-100 text-emerald-700", icon: CheckCircle2, label: "Approved" },
  pending: { color: "bg-bravura-100 text-bravura-700", icon: AlertCircle, label: "Pending" },
  rejected: { color: "bg-red-100 text-red-700", icon: XCircle, label: "Rejected" },
};

export default function RefillDetail() {
  const { id } = useParams();
  const [r, setR] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/fuel/refills/${id}`);
        setR(data);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) return <div className="text-sm text-slate-500">Loading…</div>;
  if (!r) return <div className="text-sm text-red-500">Not found</div>;

  const StatusIcon = STATUS[r.status]?.icon || AlertCircle;

  return (
    <div className="space-y-4" data-testid="refill-detail-page">
      <Link to="/driver/refills" className="inline-flex items-center gap-1.5 text-xs text-slate-500">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to list
      </Link>

      {/* Header */}
      <div className="rounded-2xl bg-gradient-to-br from-slate-900 to-slate-700 text-white p-5">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-bravura-200">Total Paid</div>
            <div className="text-3xl font-bold" style={{ fontFamily: "Chivo" }}>
              {formatINR(r.total_amount)}
            </div>
          </div>
          <div className={`px-2.5 py-1 rounded-full text-[11px] font-semibold inline-flex items-center gap-1 ${STATUS[r.status]?.color}`}>
            <StatusIcon className="h-3 w-3" />
            {STATUS[r.status]?.label || r.status}
          </div>
        </div>
        <div className="mt-3 text-xs text-slate-300 flex items-center gap-2">
          <Truck className="h-3.5 w-3.5" /> {r.truck_no}
          <span>•</span>
          <Calendar className="h-3.5 w-3.5" /> {r.refill_date}
        </div>
      </div>

      {/* Mileage callout */}
      {r.mileage_kmpl && (
        <div className="rounded-2xl bg-emerald-50 ring-1 ring-emerald-200 p-4 flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-emerald-600 text-white flex items-center justify-center">
            <Gauge className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">Mileage Achieved</div>
            <div className="text-2xl font-bold text-emerald-900" style={{ fontFamily: "Chivo" }}>
              {r.mileage_kmpl} km/L
            </div>
            <div className="text-[11px] text-emerald-700">
              {r.mileage_km_covered} km on {r.mileage_litres_consumed} L
            </div>
          </div>
        </div>
      )}
      {r.prev_mileage_kmpl && !r.mileage_kmpl && (
        <div className="rounded-2xl bg-sky-50 ring-1 ring-sky-200 p-3 text-xs text-sky-700">
          ⛽ Since last full-tank: <b>{r.prev_mileage_kmpl} km/L</b>
        </div>
      )}

      {/* Details */}
      <div className="rounded-2xl bg-white ring-1 ring-slate-200 p-4 space-y-3">
        <DetailRow label="Station" value={r.station_name || "—"} icon={MapPin} />
        <DetailRow label="Odometer" value={`${r.odometer_km?.toLocaleString()} km`} icon={Gauge} />
        <DetailRow label="Tank Full" value={r.tank_full ? "Yes ⛽" : "No"} icon={Fuel} />
        <hr className="border-slate-100" />
        <DetailRow label="Diesel" value={`${r.diesel_litres} L × ₹${r.diesel_rate} = ${formatINR(r.diesel_amount)}`} icon={Fuel} />
        {r.urea_amount && (
          <DetailRow label="Urea" value={`${r.urea_litres || "?"} L = ${formatINR(r.urea_amount)}`} icon={Fuel} />
        )}
        <hr className="border-slate-100" />
        <DetailRow label="Payment" value={r.payment_mode?.toUpperCase()} icon={CreditCard} />
        {r.upi_txn_ref && <DetailRow label="UPI Ref" value={r.upi_txn_ref} icon={CreditCard} />}
        {r.notes && (
          <div className="text-xs text-slate-600 bg-slate-50 rounded-lg p-2.5">
            <b>Notes:</b> {r.notes}
          </div>
        )}
        {r.status === "rejected" && r.approval_reason && (
          <div className="text-xs text-red-700 bg-red-50 rounded-lg p-2.5 ring-1 ring-red-200">
            <b>Rejection reason:</b> {r.approval_reason}
          </div>
        )}
      </div>

      {/* Payment proof photo */}
      {r.payment_proof_photo && (
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2">
            Payment Proof
          </div>
          <a href={r.payment_proof_photo} target="_blank" rel="noreferrer">
            <img
              src={r.payment_proof_photo}
              alt="Payment proof"
              className="w-48 aspect-square object-cover rounded-xl ring-2 ring-emerald-300"
              data-testid="refill-payment-proof"
            />
          </a>
        </div>
      )}

      {/* Photos */}
      {r.bill_photos?.length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2">
            Bill Photos
          </div>
          <div className="grid grid-cols-2 gap-2">
            {r.bill_photos.map((p, i) => (
              <a key={i} href={p} target="_blank" rel="noreferrer">
                <img src={p} alt={`bill ${i+1}`} className="aspect-square object-cover rounded-xl ring-1 ring-slate-200" />
              </a>
            ))}
          </div>
        </div>
      )}

      {/* UPI QR for paid via QR */}
      {r.payment_mode === "qr" && r.station_id && (
        <details className="rounded-2xl bg-white ring-1 ring-slate-200 p-4">
          <summary className="text-sm font-semibold text-slate-700 cursor-pointer">
            Show UPI QR
          </summary>
          <img
            src={`${API_BASE}/fuel/refills/${r.id}/qr.png`}
            alt="UPI QR"
            className="w-48 h-48 mx-auto mt-3 ring-1 ring-slate-200 rounded-xl"
            data-testid="refill-detail-qr"
          />
        </details>
      )}
    </div>
  );
}

function DetailRow({ label, value, icon: Icon }) {
  return (
    <div className="flex items-center gap-3">
      <div className="h-8 w-8 rounded-lg bg-slate-100 flex items-center justify-center text-slate-500 flex-shrink-0">
        <Icon className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
        <div className="text-sm font-semibold text-slate-900 truncate">{value}</div>
      </div>
    </div>
  );
}
